import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import MediaPicker from "@/components/social/MediaPicker";
import { MapPin, Plane, Send, Hash, CheckCircle2, ShieldCheck, ShieldAlert, AtSign, Building2, Radio, User as UserIcon, Users } from "lucide-react";

const TRIP_STATUSES = [
  { key: "preflight", label: "Preflight" },
  { key: "enroute",   label: "Enroute" },
  { key: "landed",    label: "Just Landed" },
  { key: "on_ground", label: "On Ground" },
  { key: "aog",       label: "AOG" },
];

// Rotating Hot Mic placeholder prompts — the same trick Google uses on the
// homepage search box. Picked once per composer mount so it's stable while the
// user is composing (no jitter on keystroke), but different every visit.
const HOT_MIC_PROMPTS = [
  "Go ahead…",
  "Share a lesson from today's flight…",
  "What's happening in aviation today?",
  "Where are you flying to today?",
  "The copilot did what?…",
  "Best airport diner you've hit this month?",
  "Any AOG war stories worth telling?",
  "Callout the FBO that saved your trip…",
  "Tag a crew who covered for you…",
  "Runway view from your seat right now?",
];
function pickHotMicPrompt() {
  return HOT_MIC_PROMPTS[Math.floor(Math.random() * HOT_MIC_PROMPTS.length)];
}

/**
 * PostComposer — HUD-styled, mobile-first composer.
 * Text (16px so iOS doesn't zoom), media, location tag (airport typeahead),
 * aircraft check-in (tail# verified against fleet DB), trip status.
 */
export default function PostComposer({ onCreated }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [media, setMedia] = useState([]);
  const [locOpen, setLocOpen] = useState(false);
  const [locQ, setLocQ] = useState("");
  const [locResults, setLocResults] = useState([]);
  const [loc, setLoc] = useState(null);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [tail, setTail] = useState("");
  const [tailVerified, setTailVerified] = useState(null);
  const [tripStatus, setTripStatus] = useState("");
  const [posting, setPosting] = useState(false);
  const [mentions, setMentions] = useState([]);          // FBO mentions [{handle, name, icao, id}]
  const [userMentions, setUserMentions] = useState([]);  // People mentions [{id, name, avatar_url, role}]
  const [mentionOpen, setMentionOpen] = useState(false);
  // Rotating placeholder — cycles through Hot Mic prompts every 2s.
  const [placeholder, setPlaceholder] = useState(pickHotMicPrompt);
  useEffect(() => {
    // Pause rotation while the user is typing so the prompt doesn't churn.
    if (text.length > 0) return;
    const id = setInterval(() => setPlaceholder(pickHotMicPrompt()), 2000);
    return () => clearInterval(id);
  }, [text]);
  const [mentionQ, setMentionQ] = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionCaret, setMentionCaret] = useState(null); // position of trailing '@' in text
  const tailTO = useRef(null);
  const locTO = useRef(null);
  const mentionTO = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!locQ || locQ.length < 2) { setLocResults([]); return; }
    clearTimeout(locTO.current);
    locTO.current = setTimeout(async () => {
      try {
        const r = await api.get("/airports/search", { params: { q: locQ, limit: 6 } });
        setLocResults(r.data.results || []);
      } catch { setLocResults([]); }
    }, 200);
    return () => clearTimeout(locTO.current);
  }, [locQ]);

  useEffect(() => {
    if (!tail || tail.length < 2) { setTailVerified(null); return; }
    clearTimeout(tailTO.current);
    tailTO.current = setTimeout(async () => {
      try {
        const r = await api.get("/social/aircraft/lookup", { params: { tail } });
        setTailVerified(r.data);
      } catch { setTailVerified(null); }
    }, 300);
    return () => clearTimeout(tailTO.current);
  }, [tail]);

  // Mention typeahead — detect `@word` right at the cursor
  const detectMentionQuery = (val, caret) => {
    const upto = val.slice(0, caret);
    const m = upto.match(/(?:^|\s)@([A-Za-z0-9_]{0,40})$/);
    return m ? { q: m[1], at: caret - m[1].length - 1 } : null;
  };

  const onTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const caret = e.target.selectionStart || val.length;
    const dm = detectMentionQuery(val, caret);
    if (dm) {
      setMentionOpen(true);
      setMentionQ(dm.q);
      setMentionCaret(dm.at);
    } else {
      setMentionOpen(false);
      setMentionQ("");
      setMentionCaret(null);
    }
  };

  useEffect(() => {
    if (!mentionOpen) { setMentionResults([]); return; }
    clearTimeout(mentionTO.current);
    mentionTO.current = setTimeout(async () => {
      try {
        // Query PEOPLE and FBOs in parallel. Users (Flying Buddies first) rank
        // above FBOs so @-tagging your buddy is a single tap.
        const [uRes, fRes] = await Promise.all([
          api.get("/social/users/search", { params: { q: mentionQ, limit: 6 } }).catch(() => ({ data: { results: [] } })),
          api.get("/fbos/search", { params: { q: mentionQ, limit: 4 } }).catch(() => ({ data: { results: [] } })),
        ]);
        const users = (uRes.data.results || []).map((u) => ({ ...u, _kind: "user" }));
        const fbos = (fRes.data.results || []).map((f) => ({ ...f, _kind: "fbo" }));
        setMentionResults([...users, ...fbos]);
      } catch { setMentionResults([]); }
    }, 150);
    return () => clearTimeout(mentionTO.current);
  }, [mentionOpen, mentionQ]);

  const insertUserMention = (u) => {
    if (mentionCaret === null) return;
    const before = text.slice(0, mentionCaret);
    const afterCaret = textareaRef.current?.selectionStart || (mentionCaret + mentionQ.length + 1);
    const after = text.slice(afterCaret);
    // Marker token → parsed back into a clickable Link at render time.
    // Format: @[Display Name](user:UUID) — kept human-readable so plain-text
    // reads OK too. Trailing space added for convenience.
    const insertion = `@[${u.name}](user:${u.id}) `;
    const newText = before + insertion + after;
    setText(newText);
    setUserMentions((prev) => (prev.find((m) => m.id === u.id) ? prev : [...prev, u]));
    setMentionOpen(false);
    setMentionQ("");
    setMentionCaret(null);
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + insertion.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const insertMention = (fbo) => {
    if (mentionCaret === null) return;
    const before = text.slice(0, mentionCaret);
    const afterCaret = textareaRef.current?.selectionStart || (mentionCaret + mentionQ.length + 1);
    const after = text.slice(afterCaret);
    const insertion = `@${fbo.handle} `;
    const newText = before + insertion + after;
    setText(newText);
    setMentions((prev) => (prev.find((m) => m.handle.toLowerCase() === fbo.handle.toLowerCase()) ? prev : [...prev, fbo]));
    setMentionOpen(false);
    setMentionQ("");
    setMentionCaret(null);
    // Restore focus + caret after insertion
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + insertion.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const pickMention = (item) => {
    if (item._kind === "user") insertUserMention(item);
    else insertMention(item);
  };

  const removeMention = (handle) => {
    setMentions((prev) => prev.filter((m) => m.handle !== handle));
    // Also strip from text
    setText((t) => t.replace(new RegExp(`@${handle}\\b\\s?`, "gi"), ""));
  };

  const removeUserMention = (id) => {
    setUserMentions((prev) => prev.filter((m) => m.id !== id));
    // Also strip token from text
    setText((t) => t.replace(new RegExp(`@\\[[^\\]]+\\]\\(user:${id}\\)\\s?`, "g"), ""));
  };

  const submit = async () => {
    if (!text.trim() && media.length === 0) {
      toast.error("Add some text or a photo");
      return;
    }
    setPosting(true);
    try {
      const payload = {
        text: text.trim(),
        media,
        location_icao: loc?.icao || null,
        mentions: mentions.map((m) => m.handle),
        user_mentions: userMentions.map((u) => u.id),
        checkin: (tail.trim() || tripStatus)
          ? {
              tail_number: tail.trim().toUpperCase() || null,
              aircraft_type: tailVerified?.verified ? tailVerified.aircraft_type : null,
              verified: !!tailVerified?.verified,
              aircraft_id: tailVerified?.aircraft_id || null,
              trip_status: tripStatus || null,
            }
          : null,
      };
      const r = await api.post("/social/posts", payload);
      onCreated && onCreated(r.data);
      setText(""); setMedia([]);
      setLoc(null); setLocQ(""); setLocOpen(false);
      setTail(""); setTailVerified(null); setTripStatus("");
      setCheckinOpen(false);
      setMentions([]);
      setUserMentions([]);
      toast.success("Posted to the network");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally {
      setPosting(false);
    }
  };

  if (!user) return null;

  const avatar = user.avatar_url;

  return (
    <div className="card !p-4 sm:!p-5" data-testid="post-composer">
      <div className="flex gap-3">
        <div className="flex-shrink-0">
          {avatar ? (
            <img src={avatar} alt="" className="h-11 w-11 rounded-full object-cover border border-white/15" />
          ) : (
            <div className="h-11 w-11 rounded-full bg-gradient-to-br from-sky/40 to-sky/10 border border-sky/40 grid place-items-center mono text-sm font-bold text-white">
              {(user.first_name || "?").charAt(0)}{(user.last_name || "").charAt(0)}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 relative">
          <textarea
            ref={textareaRef}
            data-testid="composer-text"
            value={text}
            onChange={onTextChange}
            placeholder={placeholder}
            rows={2}
            className="w-full bg-transparent outline-none resize-none placeholder:text-white/35 text-white leading-relaxed"
            style={{ fontSize: 16 /* stop iOS from zooming */ }}
          />

          {/* @-mention typeahead dropdown */}
          {mentionOpen && mentionResults.length > 0 && (
            <div className="absolute left-0 right-0 z-20 mt-1 rounded-xl border border-sky/40 bg-obsidian shadow-2xl overflow-hidden" data-testid="mention-typeahead">
              <div className="mono text-[10px] tracking-[0.24em] text-sky/80 uppercase px-3 pt-2.5 pb-1.5 flex items-center gap-1.5">
                <AtSign className="h-3 w-3" /> Tag a Flying Buddy or FBO
              </div>
              <div className="max-h-64 overflow-y-auto">
                {mentionResults.map((item) => (
                  <button
                    type="button"
                    key={`${item._kind}-${item.id}`}
                    onMouseDown={(e) => { e.preventDefault(); pickMention(item); }}
                    className="w-full text-left px-3 py-2.5 hover:bg-sky/10 transition-colors flex items-center gap-2.5 border-b border-white/[0.04] last:border-0"
                    data-testid={`mention-option-${item._kind}-${item.id}`}
                  >
                    {item._kind === "user" ? (
                      item.avatar_url ? (
                        <img src={item.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover border border-white/15 flex-shrink-0" />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-sky/50 to-sky/10 border border-sky/40 grid place-items-center mono text-[10px] font-bold text-white flex-shrink-0">
                          {(item.first_name || "?").charAt(0)}{(item.last_name || "").charAt(0)}
                        </div>
                      )
                    ) : (
                      <div className="h-8 w-8 rounded-lg bg-sky/15 border border-sky/30 grid place-items-center flex-shrink-0">
                        <Building2 className="h-4 w-4 text-sky" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {item._kind === "user" ? (
                        <>
                          <div className="text-white font-semibold text-sm truncate flex items-center gap-1.5">
                            {item.name}
                            {item.is_buddy && <span className="mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-avail/15 text-avail border border-avail/40">FLYING BUDDY</span>}
                          </div>
                          <div className="text-xs text-white/60 truncate">{(item.badge_label || item.role || "MEMBER")}{item.location_icao ? ` · ${item.location_icao}` : ""}</div>
                        </>
                      ) : (
                        <>
                          <div className="text-sky font-semibold text-sm mono truncate">@{item.handle}</div>
                          <div className="text-xs text-white/70 truncate">{item.name}</div>
                        </>
                      )}
                    </div>
                    <div className="mono text-[10px] text-white/40 flex-shrink-0 flex items-center gap-1">
                      {item._kind === "user" ? (
                        <><UserIcon className="h-2.5 w-2.5" /> PILOT</>
                      ) : (
                        <>{item.icao}</>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <MediaPicker media={media} setMedia={setMedia} testIdPrefix="composer" />

          {/* Mention chips — People first, then FBOs */}
          {(userMentions.length > 0 || mentions.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="composer-mentions">
              {userMentions.map((u) => (
                <span key={`u-${u.id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-cyan/[0.12] border border-cyan/40 text-cyan text-xs">
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt="" className="h-4 w-4 rounded-full object-cover" />
                  ) : (
                    <UserIcon className="h-3 w-3" />
                  )}
                  <span className="font-semibold">{u.name}</span>
                  <button type="button" onClick={() => removeUserMention(u.id)} className="ml-1 text-white/60 hover:text-white" aria-label={`Remove ${u.name}`} data-testid={`composer-remove-user-mention-${u.id}`}>×</button>
                </span>
              ))}
              {mentions.map((m) => (
                <span key={`f-${m.handle}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky/10 border border-sky/40 text-sky text-xs mono">
                  <AtSign className="h-3 w-3" />
                  {m.handle}
                  {m.icao && <span className="text-white/60">· {m.icao}</span>}
                  <button type="button" onClick={() => removeMention(m.handle)} className="ml-1 text-white/60 hover:text-white" aria-label={`Remove ${m.handle}`} data-testid={`composer-remove-mention-${m.handle}`}>×</button>
                </span>
              ))}
            </div>
          )}

          {loc && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky/10 border border-sky/40 text-sky text-xs" data-testid="composer-location-chip">
              <MapPin className="h-3.5 w-3.5" />
              <span className="mono">{loc.icao}</span>
              <span className="text-white/80">· {loc.city}, {loc.country}</span>
              <button onClick={() => setLoc(null)} className="ml-1 text-white/60 hover:text-white" aria-label="Clear location">×</button>
            </div>
          )}

          {(tail || tripStatus) && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-avail/10 border border-avail/40 text-avail text-xs" data-testid="composer-checkin-chip">
              <Plane className="h-3.5 w-3.5" />
              {tail && <span className="mono">{tail.toUpperCase()}</span>}
              {tailVerified?.verified && <CheckCircle2 className="h-3 w-3" />}
              {tripStatus && <span className="text-white/80">· {TRIP_STATUSES.find(t => t.key === tripStatus)?.label}</span>}
              <button onClick={() => { setTail(""); setTripStatus(""); setTailVerified(null); }} className="ml-1 text-white/60 hover:text-white" aria-label="Clear check-in">×</button>
            </div>
          )}

          {/* Location picker sheet */}
          {locOpen && (
            <div className="mt-3 rounded-xl border border-white/10 bg-black/40 p-3" data-testid="composer-location-panel">
              <input
                data-testid="composer-location-input"
                value={locQ}
                onChange={(e) => setLocQ(e.target.value)}
                placeholder="Tag an airport — try KTEB, Miami, Heathrow…"
                className="w-full bg-transparent outline-none border-b border-white/10 pb-2 text-white placeholder:text-white/35"
                style={{ fontSize: 16 }}
                autoFocus
              />
              {locResults.length > 0 && (
                <div className="mt-2 max-h-56 overflow-y-auto -mx-1">
                  {locResults.map((a) => (
                    <button
                      key={a.icao}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); setLoc(a); setLocOpen(false); setLocQ(""); }}
                      className="w-full text-left px-2 py-2 rounded-lg hover:bg-white/[0.06] transition-colors"
                      data-testid={`composer-loc-option-${a.icao}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="mono text-sky text-sm">{a.icao}{a.iata ? ` · ${a.iata}` : ""}</div>
                        <div className="text-[11px] text-white/50">{a.country}</div>
                      </div>
                      <div className="text-sm text-white/90">{a.name}</div>
                      <div className="text-[11px] text-white/50">{a.city}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Check-in sheet */}
          {checkinOpen && (
            <div className="mt-3 rounded-xl border border-white/10 bg-black/40 p-3 space-y-3" data-testid="composer-checkin-panel">
              <div>
                <div className="eyebrow-muted mb-1.5">Tail Number</div>
                <input
                  data-testid="composer-tail-input"
                  value={tail}
                  onChange={(e) => setTail(e.target.value.toUpperCase())}
                  placeholder="N123AB"
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-white mono placeholder:text-white/30 focus:border-sky/60 outline-none"
                  style={{ fontSize: 16 }}
                />
                {tailVerified?.verified && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-avail" data-testid="tail-verified-badge">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Verified · {tailVerified.aircraft_type}{tailVerified.operator ? ` · ${tailVerified.operator}` : ""}
                  </div>
                )}
                {tail && tailVerified && !tailVerified.verified && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-white/50" data-testid="tail-freetext-badge">
                    <ShieldAlert className="h-3.5 w-3.5" />
                    Unverified — will post as free-text
                  </div>
                )}
              </div>
              <div>
                <div className="eyebrow-muted mb-1.5">Trip Status</div>
                <div className="flex flex-wrap gap-1.5">
                  {TRIP_STATUSES.map((s) => {
                    const active = tripStatus === s.key;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        data-testid={`composer-trip-${s.key}`}
                        onClick={() => setTripStatus(active ? "" : s.key)}
                        className={`px-3 py-1.5 rounded-full text-xs mono tracking-wide border transition-colors min-h-[36px] ${
                          active
                            ? "bg-sky/25 border-sky text-white"
                            : "bg-white/[0.04] border-white/10 text-white/70 hover:border-white/25"
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Toolbar + submit */}
          <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-white/[0.06]">
            <button
              type="button"
              data-testid="composer-toggle-location"
              onClick={() => setLocOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-white/70 hover:text-white hover:bg-white/[0.04] text-sm min-h-[44px] min-w-[44px] justify-center"
            >
              <MapPin className="h-4 w-4" /> <span className="hidden sm:inline">Airport</span>
            </button>
            <button
              type="button"
              data-testid="composer-toggle-checkin"
              onClick={() => setCheckinOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-white/70 hover:text-white hover:bg-white/[0.04] text-sm min-h-[44px] min-w-[44px] justify-center"
            >
              <Plane className="h-4 w-4" /> <span className="hidden sm:inline">Check-in</span>
            </button>
            <button
              type="button"
              data-testid="composer-add-hashtag"
              onClick={() => setText((t) => (t.endsWith(" ") || t === "" ? t + "#" : t + " #"))}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-white/70 hover:text-white hover:bg-white/[0.04] text-sm min-h-[44px] min-w-[44px] justify-center"
            >
              <Hash className="h-4 w-4" /> <span className="hidden sm:inline">Tag</span>
            </button>
            <button
              type="button"
              data-testid="composer-add-mention"
              onClick={() => {
                const val = text.endsWith(" ") || text === "" ? text + "@" : text + " @";
                setText(val);
                setTimeout(() => {
                  if (textareaRef.current) {
                    textareaRef.current.focus();
                    textareaRef.current.setSelectionRange(val.length, val.length);
                    setMentionOpen(true);
                    setMentionCaret(val.length - 1);
                    setMentionQ("");
                  }
                }, 0);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sky hover:text-white hover:bg-sky/10 text-sm min-h-[44px] min-w-[44px] justify-center"
            >
              <AtSign className="h-4 w-4" /> <span className="hidden sm:inline">FBO</span>
            </button>

            {/* PTT · Rectangular tactical push-to-talk button.
                Idle: matte panel with cyan LED. TX: amber LED pulse + halo. */}
            <button
              type="button"
              data-testid="composer-submit"
              onClick={submit}
              disabled={posting || (!text.trim() && media.length === 0)}
              className={`ml-auto group relative inline-flex items-center gap-1.5 px-4 h-11 rounded-md font-bold mono transition-all disabled:opacity-30 disabled:cursor-not-allowed select-none ${posting ? "" : "hover:brightness-110 active:scale-[0.97]"}`}
              style={{
                background: "linear-gradient(180deg, #2A303A 0%, #12161E 55%, #05070C 100%)",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: posting
                  ? "0 0 0 1px rgba(244,196,67,0.55), 0 0 18px 1px rgba(244,196,67,0.45), inset 0 -2px 4px rgba(0,0,0,0.6), inset 0 1px 2px rgba(255,255,255,0.08)"
                  : "0 0 0 1px rgba(75,156,255,0.35), 0 3px 10px rgba(0,0,0,0.55), inset 0 -2px 4px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.1)",
                color: posting ? "#FFD86B" : "#7DB8FF",
              }}
              title="Push-to-Talk — broadcast to Hot Mic"
              aria-label={posting ? "Transmitting to Hot Mic" : "Push to talk"}
            >
              {/* LED status dot */}
              <span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full flex-shrink-0"
                style={{
                  background: posting ? "#FFD86B" : "#4B9CFF",
                  boxShadow: posting ? "0 0 8px 1px #FFD86B" : "0 0 6px 1px #4B9CFF",
                  animation: posting ? "ptt-led 0.6s ease-in-out infinite" : "none",
                }}
              />
              <Radio className="h-3.5 w-3.5 opacity-80" strokeWidth={2} />
              <span className="text-[11.5px] font-bold tracking-[0.18em]">
                {posting ? "TX" : "PTT"}
              </span>
            </button>
            <style>{`@keyframes ptt-led { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }`}</style>
          </div>
        </div>
      </div>
    </div>
  );
}
